import { useSecondaryPage } from '@/PageManager'
import { cn } from '@/lib/utils'
import { getParents } from '@/lib/event'
import { toNote } from '@/lib/link'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { unwrappedFeedAtom } from '@/atoms/feed'
import {
  unwrappedKnownRelaysAtom,
  unwrappedMembershipsByPubkeyAtom,
  type RelayRole
} from '@/atoms/memberships'
import { useAtomValue } from 'jotai'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { Event } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { HugeiconsIcon } from '@hugeicons/react'
import { SparklesIcon } from '@hugeicons/core-free-icons'
import { useMemo, useState } from 'react'
import AudioPlayer from '../AudioPlayer'
import Content from '../Content'
import ContentPreview from '../ContentPreview'
import FollowingBadge from '../FollowingBadge'
import PinBuryBadge from '../PinBuryBadge'
import ProtectedBadge from '../ProtectedBadge'
import { FormattedTimestamp } from '../FormattedTimestamp'
import Nip05 from '../Nip05'
import RolePill from '../RolePill'
import RelayMemberIconBadges from '../RelayMemberIconBadges'
import NoteOptions from '../NoteOptions'
import ParentNotePreview from '../ParentNotePreview'
import UserAvatar from '../UserAvatar'
import Username from '../Username'
import PendingUndoButton from '../PendingUndoButton'
import ChatMessage from './ChatMessage'
import CodeSnippet from './CodeSnippet'
import ForumThread from './ForumThread'
import GeocacheCurationList from './GeocacheCurationList'
import GeocacheFoundLog from './GeocacheFoundLog'
import GeocacheListing, { GEOCACHE_CURATION_LIST, GEOCACHE_LISTING } from './GeocacheListing'
import GeocacheVerification from './GeocacheVerification'
import GitEvent from './GitEvent'
import GroupMetadata from './GroupMetadata'
import Highlight from './Highlight'
import IValue from './IValue'
import Lists from './Lists'
import LiveEvent from './LiveEvent'
import LongFormArticle from './LongFormArticle'
import LongFormArticlePreview from './LongFormArticlePreview'
import MergeRequest from './MergeRequest'
import MutedNote from './MutedNote'
import NsfwNote from './NsfwNote'
import PictureNote from './PictureNote'
import PodcastEpisode from './PodcastEpisode'
import PodcastMetadata from './PodcastMetadata'
import Poll from './Poll'
import Reaction from './Reaction'
import UnknownNote from './UnknownNote'
import VideoNote from './VideoNote'
import WebBookmark from './WebBookmark'
import WikiArticle from './WikiArticle'
import WikiRedirect from './WikiRedirect'
import { npubEncode } from '@nostr/tools/nip19'
import { useTranslation } from 'react-i18next'
import { tagNameEquals } from '@/lib/tag'

export default function Note({
  event,
  originalNoteId,
  size = 'normal',
  className,
  hideParentNotePreview = false,
  showFull = false,
  showClientTag = false,
  hideHeader = false,
  simple = false,
  displayScore,
  clamp = false
}: {
  event: Event
  originalNoteId?: string
  size?: 'normal' | 'small'
  className?: string
  displayScore?: number
  hideParentNotePreview?: boolean
  showFull?: boolean
  showClientTag?: boolean
  hideHeader?: boolean
  simple?: boolean
  clamp?: boolean
}) {
  const { push } = useSecondaryPage()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const feed = useAtomValue(unwrappedFeedAtom)
  const knownRelays = useAtomValue(unwrappedKnownRelaysAtom)
  const memberships = useAtomValue(unwrappedMembershipsByPubkeyAtom)

  const feedRelayUrls = useMemo(() => {
    if (!feed) return undefined
    if ('hardcodedFeed' in feed) {
      return window.hallway.universe.hardcodedFeeds[feed.hardcodedFeed]?.relays
    }
    if ('relay' in feed) return [feed.relay]
    if ('relaySet' in feed) return feed.relaySet?.items
    return undefined
  }, [feed])

  const feedRoles: RelayRole[] = useMemo(() => {
    if (!event.pubkey || !feedRelayUrls?.length || !knownRelays || !memberships) return []

    const out: RelayRole[] = []
    for (const url of feedRelayUrls) {
      if (!knownRelays.includes(url)) continue

      const inner = memberships.get(event.pubkey)
      if (!inner) continue

      const rs = inner.get(url)
      if (!rs) continue

      for (const r of rs) out.push(r)
    }
    if (out.length <= 1) return out
    return out.slice().sort((a, b) => {
      const ao = a.order ?? Number.POSITIVE_INFINITY
      const bo = b.order ?? Number.POSITIVE_INFINITY
      if (ao !== bo) return ao - bo
      return a.id.localeCompare(b.id)
    })
  }, [event.pubkey, feedRelayUrls, knownRelays, memberships])

  const [showNsfw, setShowNsfw] = useState(false)
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const [showMuted, setShowMuted] = useState(false)
  const { t } = useTranslation()

  const parents = useMemo(
    () => (hideParentNotePreview ? [] : getParents(event)),
    [event, hideParentNotePreview]
  )

  let content: React.ReactNode
  if (simple) {
    content = (
      <div className="mt-2 truncate">
        <ContentPreview event={event} />
      </div>
    )
  } else if (mutePubkeySet.has(event.pubkey) && !showMuted) {
    content = <MutedNote show={() => setShowMuted(true)} />
  } else if (
    event.tags.some(
      ([t, v]) => t === 'content-warning' || (t === 't' && v?.toLowerCase() === 'nsfw')
    ) &&
    !showNsfw
  ) {
    content = <NsfwNote show={() => setShowNsfw(true)} />
  } else {
    switch (event.kind) {
      case kinds.ShortTextNote:
      case kinds.Comment:
        content = (
          <Content
            className={cn('mt-2', showFull && 'font-serif text-lg')}
            event={event}
            clamp={clamp}
          />
        )
        break
      case kinds.ChatMessage:
        content = <ChatMessage className="mt-2" event={event} clamp={clamp} />
        break
      case kinds.ForumThread:
        content = <ForumThread className="mt-2" event={event} clamp={clamp} />
        break
      case kinds.CodeSnippet:
        content = <CodeSnippet className="mt-2" event={event} />
        break
      case kinds.PodcastMetadata:
        content = <PodcastMetadata className="mt-2" event={event} />
        break
      case kinds.PodcastEpisode:
        content = <PodcastEpisode className="mt-2" event={event} />
        break
      case kinds.WikiArticle:
        content = <WikiArticle className="mt-2" event={event} />
        break
      case kinds.Redirects:
        content = <WikiRedirect className="mt-2" event={event} />
        break
      case kinds.MergeRequests:
        content = <MergeRequest className="mt-2" event={event} />
        break
      case GEOCACHE_LISTING:
        content = <GeocacheListing className="mt-2" event={event} />
        break
      case kinds.GeocacheLog:
        content = <GeocacheFoundLog className="mt-2" event={event} />
        break
      case kinds.GeocacheProofOfFind:
        content = <GeocacheVerification className="mt-2" event={event} />
        break
      case GEOCACHE_CURATION_LIST:
        content = <GeocacheCurationList className="mt-2" event={event} />
        break
      case kinds.Highlights:
        content = (
          <Highlight className={cn('mt-2', showFull && 'font-serif text-lg')} event={event} />
        )
        break
      case kinds.LongFormArticle:
        content = showFull ? (
          <LongFormArticle className="mt-2 font-serif text-lg" event={event} />
        ) : (
          <LongFormArticlePreview className="mt-2" event={event} />
        )
        break
      case kinds.LiveEvent:
        content = <LiveEvent className="mt-2" event={event} />
        break
      case kinds.GroupMetadata:
        content = <GroupMetadata className="mt-2" event={event} originalNoteId={originalNoteId} />
        break
      case kinds.Poll:
        content = (
          <>
            <Content
              className={cn('mt-2', showFull && 'font-serif text-lg')}
              event={event}
              clamp={clamp}
            />
            <Poll className="mt-2" event={event} />
          </>
        )
        break
      case kinds.Voice:
      case kinds.VoiceComment:
        content = <AudioPlayer className="mt-2" src={event.content} />
        break
      case kinds.Photo:
        content = <PictureNote className="mt-2" event={event} />
        break
      case kinds.NormalVideo:
      case kinds.ShortVideo:
        content = <VideoNote className="mt-2" event={event} />
        break
      case kinds.WebBookmarks:
        content = <WebBookmark className="mt-2" event={event} />
        break
      case kinds.Patch:
      case kinds.GitPullRequest:
      case kinds.GitPullRequestUpdate:
      case kinds.Issue:
      case kinds.StatusOpen:
      case kinds.StatusApplied:
      case kinds.StatusClosed:
      case kinds.StatusDraft:
      case kinds.UserGraspList:
      case kinds.RepositoryAnnouncement:
      case kinds.RepositoryState:
        content = <GitEvent className="mt-2" event={event} />
        break
      case kinds.Reaction:
        content = <Reaction className="mt-2" event={event} />
        break
      case kinds.PublicMessage:
        const recipient = event.tags.find((t) => t[0] === 'p')
        if (recipient) {
          event.content = `nostr:${npubEncode(recipient[1])}` + event.content
          content = (
            <>
              <div className="text-sm text-muted-foreground font-medium">Public Message</div>
              <Content
                className={cn('mt-2', showFull && 'font-serif text-lg')}
                event={event}
                clamp={clamp}
              />
            </>
          )
        }
        break
      case kinds.Contacts:
      case kinds.Mutelist:
      case kinds.Pinlist:
      case kinds.RelayList:
      case kinds.BookmarkList:
      case kinds.CommunitiesList:
      case kinds.PublicChatsList:
      case kinds.BlockedRelaysList:
      case kinds.SearchRelaysList:
      case kinds.ProfileBadges:
      case kinds.SimpleGroupList:
      case kinds.FavoriteRelays:
      case kinds.PrivateEventRelayList:
      case kinds.InterestsList:
      case kinds.MediaFollows:
      case kinds.UserEmojiList:
      case kinds.DirectMessageRelaysList:
      case kinds.BlossomServerList:
      case kinds.GoodWikiAuthorList:
      case kinds.GoodWikiRelayList:
      case kinds.FavoritePodcasts:
      case kinds.AuthoredPodcasts:
      case 10017:
      case 10018:
      case kinds.Followsets:
      case kinds.Relaysets:
      case kinds.Bookmarksets:
      case kinds.Curationsets:
      case kinds.CuratedVideoSets:
      case kinds.MuteSets:
      case kinds.Interestsets:
      case kinds.Emojisets:
      case kinds.ReleaseArtifactSets:
      case kinds.AppCurationSet:
      case kinds.Calendar:
      case kinds.StarterPacks:
      case kinds.MediaStarterPacks:
      case 30006:
        content = <Lists className="mt-2" event={event} />
        break
      default:
        content = <UnknownNote className="mt-2" event={event} />
        break
    }
  }

  const parentsStyle = useMemo(
    () => ({ gridTemplateColumns: `repeat(${parents.length}, minmax(0, 1fr))` }),
    [parents]
  )

  return (
    <div className={className}>
      {!hideHeader && (
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center space-x-2 flex-1">
            <UserAvatar userId={event.pubkey} size={size === 'small' ? 'medium' : 'normal'} />
            <div className="flex-1 w-0">
              <div className="flex gap-0.5 items-center">
                <Username
                  userId={event.pubkey}
                  className={`font-semibold flex truncate ${size === 'small' ? 'text-sm' : ''}`}
                  skeletonClassName={size === 'small' ? 'h-3' : 'h-4'}
                />
                <FollowingBadge pubkey={event.pubkey} />
                <RelayMemberIconBadges pubkey={event.pubkey} />
                <PinBuryBadge pubkey={event.pubkey} />
                <ProtectedBadge event={event} />
                {(showFull || showClientTag) && event.tags.find(tagNameEquals('client')) && (
                  <span className="text-sm text-muted-foreground shrink-0">
                    {t('via {{client}}', { client: event.tags.find(tagNameEquals('client'))?.[1] })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                {feedRoles.length > 0 ? (
                  <div className="flex items-center gap-1 flex-wrap min-w-0">
                    {feedRoles.map((r, i) => (
                      <RolePill key={`${r.id}:${i}`} role={r} />
                    ))}
                    <span className="text-sm text-muted-foreground">·</span>
                  </div>
                ) : (
                  <Nip05 pubkey={event.pubkey} append="·" />
                )}
                <FormattedTimestamp
                  timestamp={event.created_at}
                  className="shrink-0"
                  short={isSmallScreen}
                  href={toNote(originalNoteId ?? event)}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    push(toNote(originalNoteId ?? event))
                  }}
                />
                {typeof displayScore === 'number' && (
                  <>
                    <span>·</span>
                    <span className="shrink-0 text-primary font-medium flex items-center gap-0.5">
                      <HugeiconsIcon icon={SparklesIcon} size={14} />
                      {Math.round(displayScore)}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          {size === 'normal' && (
            <NoteOptions event={event} className="py-1 shrink-0 [&_svg]:size-5" />
          )}
        </div>
      )}
      <PendingUndoButton event={event} className="mt-1" />
      {!simple && !!parents.length && (
        <div className="grid gap-2 shrink-0" style={parentsStyle}>
          {parents.map((parent, i) => (
            <ParentNotePreview
              key={
                'value' in parent ? parent.value : 'id' in parent ? parent.id : parent.identifier
              }
              first={i === 0}
              parent={parent}
              className="mt-2"
              onClick={(e) => {
                e.stopPropagation()
                if ('value' in parent) {
                  /* push(toNoteList({ external: parent })) */
                } else push(toNote(parent))
              }}
            />
          ))}
        </div>
      )}
      <IValue event={event} className="mt-2" />
      {content}
    </div>
  )
}
